import { CHUNK_SIZE, PARCEL_SIZE, START_PARCELS_X, START_PARCELS_Y } from './constants';

/**
 * Network kinds. Highway is laid at map creation and cannot be built or removed by players.
 * Service roads are the airside's own narrow roads: ground vehicles, the fire service and the
 * airport's vans use them (and prefer them); passengers' cars and through traffic never do.
 */
export const NetworkKind = { None: 0, Road: 1, Taxiway: 2, Highway: 3, Service: 4 } as const;
export type NetworkKindId = (typeof NetworkKind)[keyof typeof NetworkKind];

/** Low bits of a network tile hold the kind; this flag marks the painted centre line. */
export const NETWORK_KIND_MASK = 0x0f;
export const NETWORK_CENTERLINE = 0x10;
/** A toll plaza across the road here (see commands.ts setToll): on road tiles only. */
export const NETWORK_TOLL = 0x80;
/**
 * A service road runs under the taxiway here, in a tunnel (the same bit as NETWORK_TOLL, on
 * taxiway tiles only): laid where a service road is drawn across a taxiway (see buildNetwork).
 */
export const NETWORK_TUNNEL = 0x80;

/**
 * A service road runs under the road here, in a tunnel (the same bit as NETWORK_CENTERLINE,
 * which road tiles never carry): laid where a service road is drawn across a road. Cars drive
 * on over it; the airport's vehicles pass below (see service-graph.ts).
 */
export const NETWORK_ROAD_TUNNEL = 0x10;

/** Whether a network tile is a taxiway with a service road tunnel under it. */
export function isTaxiwayTunnel(value: number): boolean {
  return (value & NETWORK_KIND_MASK) === NetworkKind.Taxiway && (value & NETWORK_TUNNEL) !== 0;
}

/** Whether a network tile is a road with a service road tunnel under it. */
export function isRoadTunnel(value: number): boolean {
  return (value & NETWORK_KIND_MASK) === NetworkKind.Road && (value & NETWORK_ROAD_TUNNEL) !== 0;
}

/** Whether a service road tunnel runs under a network tile (a taxiway's or a road's). */
export function isServiceTunnel(value: number): boolean {
  return isTaxiwayTunnel(value) || isRoadTunnel(value);
}

/**
 * A public road runs under the taxiway here, in a tunnel (a bit taxiway tiles never use; on road
 * tiles it is part of a junction's kind): laid where a road is drawn across a taxiway, or a
 * taxiway across a road (see buildNetwork). Aircraft taxi on over it; cars drive through below,
 * straight along the road. A taxiway tile carries one tunnel at most (this or NETWORK_TUNNEL).
 */
export const NETWORK_TAXI_ROAD_TUNNEL = 0x20;

/** Whether a network tile is a taxiway with a public road's tunnel under it. */
export function isTaxiwayRoadTunnel(value: number): boolean {
  return (
    (value & NETWORK_KIND_MASK) === NetworkKind.Taxiway && (value & NETWORK_TAXI_ROAD_TUNNEL) !== 0
  );
}

/**
 * Per tile, the axis the road tunnel under a taxiway there runs along (0 along x, 1 along y):
 * the one on which the tunnel leads out to the road at both ends (else at one end); 2 where it
 * leads out nowhere; -1 off the tunnels. Null where there are none.
 */
export function roadTunnelAxes(
  network: Uint8Array,
  width: number,
  height: number,
): Int8Array | null {
  let axes: Int8Array | null = null;
  for (let t = 0; t < network.length; t++) {
    if (!isTaxiwayRoadTunnel(network[t] as number)) continue;
    axes ??= new Int8Array(network.length).fill(-1);
    const x0 = t % width;
    const y0 = (t - x0) / width;
    let best = 2;
    let bestEnds = 0;
    for (const axis of [0, 1]) {
      let ends = 0;
      for (const s of [-1, 1]) {
        let x = x0;
        let y = y0;
        for (;;) {
          x += axis === 0 ? s : 0;
          y += axis === 1 ? s : 0;
          if (x < 0 || y < 0 || x >= width || y >= height) break;
          const v = network[y * width + x] as number;
          if (isTaxiwayRoadTunnel(v)) continue;
          const kind = v & NETWORK_KIND_MASK;
          if (kind === NetworkKind.Road || kind === NetworkKind.Highway) ends++;
          break;
        }
      }
      if (ends > bestEnds) {
        best = axis;
        bestEnds = ends;
      }
    }
    axes[t] = best;
  }
  return axes;
}

/**
 * Whether cars may step between neighbouring tiles a and b (a step along `axis`) as far as the
 * road tunnels under taxiways go (see roadTunnelAxes): into, out of and on through one only
 * along it, and from lane to lane within it.
 */
export function roadTunnelLink(
  axes: Int8Array | null,
  a: number,
  b: number,
  axis: number,
): boolean {
  if (!axes) return true;
  const ta = axes[a] as number;
  const tb = axes[b] as number;
  if (ta < 0 && tb < 0) return true;
  if (ta >= 0 && tb >= 0) return ta === tb;
  return (ta < 0 ? tb : ta) === axis;
}

/** The motorway past the airport: 4 lanes, running east–west along the starting land. */
export const HIGHWAY_WIDTH = 4;

/*
 * The through motorway itself runs on a viaduct a little way south of that band (the band is the
 * at-grade road the airport's roads meet). Where the airport first joins the band, a link road
 * (motorway, like the band: the state's) runs south from that junction, under the viaduct, and
 * the interchange's ramps come down to it either side. It is laid once and stays.
 */

/** The viaduct's centre line: this many tiles south of the band's first row. */
export const VIADUCT_OFFSET = 20;
/** Ground kept clear of buildings and taxiways: this far either side of the viaduct's centre
 * line all along it, and this far where the interchange's ramps run beside it. */
export const VIADUCT_CLEAR = 4;
export const RAMPS_CLEAR = 7;
/** How far either side of the link road the ramps run. */
export const RAMPS_REACH = 34;
/** The link road: as wide as the band, from the band to this far past the viaduct's centre. */
export const LINK_WIDTH = 4;
export const LINK_BEYOND = 7;

/** The viaduct's centre line for a band starting at `row`, or -1 if the map has no room for it. */
export function viaductLine(row: number, height: number): number {
  if (row < 0) return -1;
  const line = row + VIADUCT_OFFSET;
  return line + LINK_BEYOND <= height ? line : -1;
}

/** First column of the link road (motorway tiles right below the band), or -1 if none yet. */
export function linkRoadX(network: Uint8Array, width: number, row: number): number {
  const y = row + HIGHWAY_WIDTH;
  if (row < 0 || (y + 1) * width > network.length) return -1;
  for (let x = 0; x < width; x++) {
    if (((network[y * width + x] as number) & NETWORK_KIND_MASK) === NetworkKind.Highway) return x;
  }
  return -1;
}

/** Whether the viaduct or the interchange's ramps stand over a tile (see viaductLine). */
export function underMotorway(x: number, y: number, line: number, link: number): boolean {
  if (line < 0) return false;
  const dz = Math.abs(2 * y + 1 - 2 * line);
  if (dz < 2 * VIADUCT_CLEAR) return true;
  return (
    link >= 0 &&
    dz < 2 * RAMPS_CLEAR &&
    x >= link - RAMPS_REACH &&
    x < link + LINK_WIDTH + RAMPS_REACH
  );
}

/**
 * The map surface: which parcels are owned, which tiles carry a road or taxiway, and which
 * building occupies each tile. Tiles are addressed (x, y), x growing east and y growing south.
 */
export class Ground {
  readonly parcelsX: number;
  readonly parcelsY: number;
  /** 1 if the parcel is owned. Index: py * parcelsX + px. */
  readonly owned: Uint8Array;
  /** NetworkKind | NETWORK_CENTERLINE per tile. */
  readonly network: Uint8Array;
  /**
   * Building id per tile (0 = free). Derived from the building list: rebuilt on load, never
   * saved or hashed separately.
   */
  readonly occupancy: Uint16Array;
  readonly chunksX: number;
  readonly chunksY: number;

  /** Render bookkeeping only: not simulation state, never hashed or saved. */
  private readonly dirtyChunks = new Set<number>();
  parcelsDirty = true;

  constructor(
    readonly width: number,
    readonly height: number,
  ) {
    this.parcelsX = Math.ceil(width / PARCEL_SIZE);
    this.parcelsY = Math.ceil(height / PARCEL_SIZE);
    this.owned = new Uint8Array(this.parcelsX * this.parcelsY);
    this.network = new Uint8Array(width * height);
    this.occupancy = new Uint16Array(width * height);
    this.chunksX = Math.ceil(width / CHUNK_SIZE);
    this.chunksY = Math.ceil(height / CHUNK_SIZE);
  }

  /** The centred starting block of parcels: first parcel and size. */
  private startingBlock() {
    const sx = Math.min(START_PARCELS_X, this.parcelsX);
    const sy = Math.min(START_PARCELS_Y, this.parcelsY);
    return {
      px0: Math.floor((this.parcelsX - sx) / 2),
      py0: Math.floor((this.parcelsY - sy) / 2),
      sx,
      sy,
    };
  }

  /** Marks the centred starting block of parcels as owned. */
  grantStartingParcels(): void {
    const { px0, py0, sx, sy } = this.startingBlock();
    for (let py = py0; py < py0 + sy; py++) {
      for (let px = px0; px < px0 + sx; px++) this.owned[py * this.parcelsX + px] = 1;
    }
  }

  /** First tile row of the motorway: right below the starting land. */
  highwayRow(): number {
    const { py0, sy } = this.startingBlock();
    return Math.min((py0 + sy) * PARCEL_SIZE, this.height - HIGHWAY_WIDTH);
  }

  /**
   * Lays the motorway across the whole map, just outside the starting land, on every tile that
   * is still free. Passengers and cars reach the airport from it.
   */
  layHighway(): void {
    const y0 = this.highwayRow();
    for (let y = y0; y < y0 + HIGHWAY_WIDTH; y++) {
      for (let x = 0; x < this.width; x++) {
        const i = this.index(x, y);
        if (this.network[i] === 0 && this.occupancy[i] === 0)
          this.setNetwork(x, y, NetworkKind.Highway);
      }
    }
  }

  /** The viaduct's centre line (-1: none on this map). */
  viaductLine(): number {
    return viaductLine(this.highwayRow(), this.height);
  }

  /** Tiles the viaduct and its ramps stand over: nothing but roads may go there. */
  motorwayClear(): (x: number, y: number) => boolean {
    const line = this.viaductLine();
    const link = line < 0 ? -1 : linkRoadX(this.network, this.width, this.highwayRow());
    return (x, y) => underMotorway(x, y, line, link);
  }

  /**
   * The interchange: once a road first meets the band from the airport's side, the link road
   * south from that junction to the viaduct (centred on the first such road from the west).
   * Laid once; not if something already stands in its way.
   */
  layInterchange(): void {
    const row = this.highwayRow();
    const line = viaductLine(row, this.height);
    if (line < 0 || row < 1 || linkRoadX(this.network, this.width, row) >= 0) return;
    let a = -1;
    let b = -1;
    for (let x = 0; x < this.width; x++) {
      const meets =
        this.networkKindAt(x, row - 1) === NetworkKind.Road &&
        this.networkKindAt(x, row) === NetworkKind.Highway;
      if (meets) {
        if (a < 0) a = x;
        b = x;
      } else if (a >= 0) break;
    }
    if (a < 0) return;
    const x0 = Math.max(
      0,
      Math.min(this.width - LINK_WIDTH, a + Math.floor((b - a + 1 - LINK_WIDTH) / 2)),
    );
    const y0 = row + HIGHWAY_WIDTH;
    const y1 = line + LINK_BEYOND;
    for (let y = y0; y < y1; y++) {
      for (let x = x0; x < x0 + LINK_WIDTH; x++) {
        if (this.networkAt(x, y) !== 0 || this.occupantAt(x, y) !== 0) return;
      }
    }
    for (let y = y0; y < y1; y++) {
      for (let x = x0; x < x0 + LINK_WIDTH; x++) this.setNetwork(x, y, NetworkKind.Highway);
    }
  }

  inBounds(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < this.width && y < this.height;
  }

  index(x: number, y: number): number {
    return y * this.width + x;
  }

  parcelOwned(px: number, py: number): boolean {
    if (px < 0 || py < 0 || px >= this.parcelsX || py >= this.parcelsY) return false;
    return this.owned[py * this.parcelsX + px] === 1;
  }

  tileOwned(x: number, y: number): boolean {
    return this.parcelOwned(Math.floor(x / PARCEL_SIZE), Math.floor(y / PARCEL_SIZE));
  }

  ownedCount(): number {
    let n = 0;
    for (const v of this.owned) n += v;
    return n;
  }

  setOwned(px: number, py: number): void {
    this.owned[py * this.parcelsX + px] = 1;
    this.parcelsDirty = true;
  }

  networkAt(x: number, y: number): number {
    return this.network[this.index(x, y)] as number;
  }

  networkKindAt(x: number, y: number): number {
    if (!this.inBounds(x, y)) return NetworkKind.None;
    return (this.network[this.index(x, y)] as number) & NETWORK_KIND_MASK;
  }

  setNetwork(x: number, y: number, value: number): void {
    this.network[this.index(x, y)] = value;
    // Edges and centre lines depend on neighbours, so their chunks need a rebuild too.
    this.markTile(x, y);
    this.markTile(x - 1, y);
    this.markTile(x + 1, y);
    this.markTile(x, y - 1);
    this.markTile(x, y + 1);
  }

  occupantAt(x: number, y: number): number {
    return this.occupancy[this.index(x, y)] as number;
  }

  fillOccupancy(x: number, y: number, w: number, h: number, id: number): void {
    for (let ty = y; ty < y + h; ty++) {
      this.occupancy.fill(id, this.index(x, ty), this.index(x + w, ty));
    }
  }

  chunkKey(cx: number, cy: number): number {
    return cy * this.chunksX + cx;
  }

  decodeChunkKey(key: number): { cx: number; cy: number } {
    return { cx: key % this.chunksX, cy: Math.floor(key / this.chunksX) };
  }

  private markTile(x: number, y: number): void {
    if (!this.inBounds(x, y)) return;
    this.dirtyChunks.add(this.chunkKey(Math.floor(x / CHUNK_SIZE), Math.floor(y / CHUNK_SIZE)));
  }

  markAllDirty(): void {
    for (let cy = 0; cy < this.chunksY; cy++) {
      for (let cx = 0; cx < this.chunksX; cx++) this.dirtyChunks.add(this.chunkKey(cx, cy));
    }
    this.parcelsDirty = true;
  }

  drainDirtyChunks(): number[] {
    const keys = [...this.dirtyChunks];
    this.dirtyChunks.clear();
    return keys;
  }
}
